import { Controller } from "@hotwired/stimulus";

/**
 * キーバインド入力などで、許可した英数字以外の入力を抑止する controller。
 *
 * targets:
 * - keyboard: 制限対象の input（複数）
 * - error: エラーメッセージ表示領域（未使用でも保持）
 *
 * values:
 * - allowedChars: 許可する文字クラス（正規表現の文字クラス内で使う前提）
 *   例: "A-Za-z" / "0-9" / "A-Za-z0-9_"
 *
 * 挙動:
 * - beforeinput / input / keydown を併用して、特殊キーや不正文字の入力を防止
 * - IME合成開始時は一時的に input を disabled にして合成入力を抑止
 * - 不正入力時に `.is-error` を付与
 */
export default class extends Controller {
  static targets = ["keyboard", "error"];
  static values = {
    allowedChars: { type: String, default: "A-Za-z" },
  };

  connect() {
    this.unwantedPattern = new RegExp(`[^${this.allowedCharsValue}]`, "g");

    this.beforeinputEventListener = this.beforeinputEvent.bind(this);
    this.inputEventListener = this.inputEvent.bind(this);
    this.keyDownEventListener = this.keyDownEvent.bind(this);
    this.keyboardTargets.forEach(input => {
      input.previousValue = input.value;
      input.addEventListener("beforeinput", this.beforeinputEventListener);
      input.addEventListener("input", this.inputEventListener);
      input.addEventListener("keydown", this.keyDownEventListener);
      input.addEventListener("blur", this.hideErrorHighlight.bind(this, input));
      input.addEventListener("compositionstart", this.restrictComposition.bind(this, input));
      this.hideError(input);
    }, this);

    // $(document).on("opening", "[data-remodal-id=modalKeyBind]", this.hideErrorMessage.bind(this));
  }

  disconnect() {
    this.keyboardTargets.forEach(input => {
      input.removeEventListener("beforeinput", this.beforeinputEventListener);
      input.removeEventListener("input", this.inputEventListener);
      input.removeEventListener("keydown", this.keyDownEventListener);
      input.removeEventListener("blur", this.hideErrorHighlight);
    }, this);

    // $(document).off("opening", "[data-remodal-id=modalKeyBind]", this.hideErrorMessage);
  }

  beforeinputEvent(event) {
    event.target.previousValue = event.target.value;
  }

  inputEvent(event) {
    event.target.value = event.target.previousValue;
  }

  keyDownEvent(event) {
    const key = event.key;
    if (!this.checkInput(key, event.target)) {
      event.preventDefault();
    }
  }

  restrictComposition(input) {
    input.disabled = true;
    requestAnimationFrame(() => {
      input.disabled = false;
      input.focus();
    });
  }

  checkInput(key, element) {
    if (key.length > 1) {
      // 制御コードや特殊キーの場合
      this.showError(element);
      return false;
    }

    const filtered = key.replace(this.unwantedPattern, "");
    if (filtered !== key) {
      this.showError(element);
      return false;
    }

    this.hideError(element);
    return true;
  }

  showError(element) {
    // this.showErrorMessage();
    this.showErrorHighlight(element);
  }

  hideError(element) {
    // this.hideErrorMessage();
    this.hideErrorHighlight(element);
  }

  showErrorMessage() {
    this.errorTarget.style.opacity = "1";
  }

  hideErrorMessage() {
    this.errorTarget.style.opacity = "0";
  }

  showErrorHighlight(element) {
    element.classList.add("is-error");
  }

  hideErrorHighlight(element) {
    element.classList.remove("is-error");
  }
}
